/**
 * @lines98/client/app — app root + screen router.
 *
 * Single host element (#app) — screens mount / unmount as `phase` changes.
 * No router framework (vanilla TS per ADR-0001 §A2). URL params:
 *   - `?seed=<uint32>` — start с the given seed (casual; dev/QA reproducibility).
 *   - `?mode=casual` — opt out от daily and start casual directly (dev/QA).
 *
 * Phase transitions are driven by the Zustand store; this module owns the
 * mount/unmount lifecycle. Each screen returns a `ScreenHandle` whose
 * `unmount()` is called before the next screen mounts.
 *
 * Screens (T-5 + T-7):
 *   - `daily` — daily-board hub (idle / done states).
 *   - `board` — live play (used by both casual and daily attempt-in-progress).
 *   - `result` — end-of-game summary.
 *   - `replay` — playback.
 *
 * Default boot phase (owner-decision 2026-06-11, fix/play-as-start-page):
 * `modeSelect` — the «Играть» picker (lobby of boards: doska dnya via the nav-rail
 * + extra modes), NOT the daily board directly. Owner reason: landing on the picker
 * gives a time buffer to PREWARM the daily-board status in the background (seed +
 * played/idle snapshot + leaderboard) so the FIRST visit to the daily board is WARM
 * (no cold skeleton / idle→done flicker) — see `services/daily-status-prefetch`.
 * URL params (`?seed=` or `?mode=casual`) still override и boot straight into casual;
 * deep-links (`/r/<short_id>`, OAuth callback, `?demo=`, `?dev=daily`) keep their own
 * entry points untouched (they branch BEFORE this default).
 */

import { UINT32_MAX } from '@lines98/shared';
import { genBotMoves } from '@lines98/shared/engine/lines98';
import type { ColorIndex } from '@lines98/shared/types/game';
import { dateOfDayFromUtc } from '@lines98/shared/util';

import { mountSettingsDrawer } from './components/settings-drawer.js';
import { preloadMetaAssets } from './lib/asset-preload.js';
import { logout as authLogout, openLoginFlow } from './lib/auth-flow.js';
import { isDevDaily } from './lib/dev-flags.js';
import { onIdentityChanged } from './lib/identity-events.js';
import { ACHIEVEMENT_DISPLAY } from './lib/achievement-catalog.js';
import { hasUnseenAchievements, loadSeenAchievements } from './lib/achievement-seen-badge.js';
import { hasUnseenUnlocks, loadLastSeenUnlocks } from './lib/gnome-unlock-badge.js';
import { mountHelperUnlockOverlay } from './lib/helper-unlock-overlay.js';
import { loadMetaSnapshot } from './lib/meta-snapshot.js';
import { onMetaStateGranted } from './lib/meta-state-events.js';
import { resumeOrLobby } from './lib/run-resume.js';
import {
  mountNavRail,
  type NavRailAccountIdentity,
  type NavRailHandle,
  type NavRailItem,
} from './lib/nav-rail.js';
import { installSessionRevokedBroadcast } from './lib/session-revoked-broadcast.js';
import { installSessionHeartbeat } from './lib/session-heartbeat.js';
import {
  consumeOpenLoginFlag,
  installSessionRevokedBanner,
  showSessionRevokedBanner,
} from './lib/session-revoked-banner.js';
import { clearPlayed, clearInProgress } from './services/daily-cache.js';
import { getCurrent, isAuthed } from './services/identity.js';
import { playerDisplayParts } from '@lines98/shared/util';
import { mountBoardScreen, type ScreenHandle } from './screens/board-screen.js';
import { mountDailyBoardScreen } from './screens/daily-board-screen.js';
import { mountLeaderboardScreen, type LeaderboardData } from './screens/leaderboard-screen.js';
import { mountRemoteReplay } from './screens/remote-replay.js';
import {
  mountReplayScreen,
  type ForeignSaveOptions,
  type OwnShareOptions,
} from './screens/replay-screen.js';
import { mountReplayTeaserScreen } from './screens/replay-teaser-screen.js';
import { mountResultScreen } from './screens/result-screen.js';
import { mountSettingsScreen } from './screens/settings-screen.js';
// mountWorkshopScreen УДАЛЁН вместе с «животными» (workshop-animals-removal.md):
// экран зверей снят; пункт «Мастерская» в рейле теперь disabled; /workshop graceful.
import { mountGnomesScreen } from './screens/gnomes-screen.js';
import { mountProfileScreen } from './screens/profile-screen.js';
import { mountBookScreen } from './screens/book-screen.js';
import { mountSupportScreen } from './screens/support-screen.js';
import { mountModeSelectScreen } from './screens/mode-select-screen.js';
import { mountStartScreen } from './screens/start-screen.js';
import { mountContentScreen } from './screens/content-screen.js';
import { mountHelpFigureHarness } from './lib/help-figures-harness.js';
import {
  classifyBootPath,
  navigateContent,
} from './lib/content-routing.js';
import {
  canonicalEnRedirect,
  contentPath,
  isSiteRoot,
  parseContentPath,
} from '@lines98/shared/config/content-routes';
import { isContentLang } from './lib/content-routing.js';
import {
  applyGameRoute,
  classifyGamePath,
  isRetiredGamePath,
  pathForPhase,
  GAME_ROUTE_PATHS,
} from './lib/game-routing.js';
import { legacyAppRouteRedirect } from '@lines98/shared/config/app-routes';
import { syncRobotsMeta } from './lib/robots-meta.js';
import { formatDateLabel } from './lib/format.js';
import { applyLocale, LOCALE_CATALOG_READY_EVENT, resolveRootRedirectSync, setLocale, syncLocaleOnLogin, t } from './lib/i18n/index.js';
import type { UiLang } from '@lines98/shared/config/seo';
import { useGameStore, type Phase } from './store/game-store.js';
import { exposeStoreToWindow } from './ui/dev-exposure.js';
import { showToast } from './ui/toast.js';

interface RuntimeRefs {
  host: HTMLElement;
  current: { handle: ScreenHandle; phase: Phase } | null;
}

let runtime: RuntimeRefs | null = null;

/**
 * Set by `bootShell` — opens the singleton settings drawer. Used by the
 * `?demo=settingsdrawer` viewport route to show the drawer-open state.
 */
let pendingSettingsDrawerOpen: (() => void) | null = null;

/**
 * Hook для demo-маршрута `?demo=appearance` (appearance-skins.md §5): открыть
 * settings-drawer СРАЗУ на под-экране «Оформление» для viewport-baseline.
 */
let pendingAppearanceOpen: (() => void) | null = null;

/**
 * Parse `?seed=<uint32>` for QA reproducibility (T-5 brief: «для дев/QA: query
 * param ?seed=12345 → используй заданный»). Returns null if absent / invalid;
 * caller falls back к `Math.random()` через store.
 */
function parseSeedFromQuery(): number | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('seed');
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0 || n > UINT32_MAX) return null;
  return n >>> 0;
}

/**
 * Determine the initial phase for the app boot.
 *   - If `?seed=` URL param present → casual (dev/QA reproducibility).
 *   - If `?mode=casual` URL param present → casual.
 *   - Otherwise → `modeSelect`, the «Играть» picker (owner-decision 2026-06-11,
 *     fix/play-as-start-page). Was `daily` (T-7 dispatch §2 C8) — superseded so the
 *     landing is the board picker and the daily-board status can warm in the
 *     background while the player is on the picker (see module header).
 */
function decideInitialPhase(): { phase: Phase; querySeed: number | null } {
  const querySeed = parseSeedFromQuery();
  const params = new URLSearchParams(window.location.search);
  const modeParam = params.get('mode');
  if (querySeed !== null || modeParam === 'casual') {
    return { phase: 'board', querySeed };
  }
  // Дефолт голого `/` — СТАРТОВАЯ-ЛЕНДИНГ (единая индексируемая главная,
  // fix/i18n-unify-homepage). Скролл-лендинг (hero/Играть/Правила/Доска дня/Лидерборд/
  // CTA), НЕ лобби. Лобби живёт на `/play` (рейл «Играть»). Boot ниже распознаёт голый
  // лендинг-путь и сетит язык по URL-модели B (en→`/`, ru→`/ru/`).
  return { phase: 'start', querySeed: null };
}

/**
 * Голый лендинг-путь под URL-модель B = БЕЗ slug (только язык или корень): `/`
 * (en-главная) или `/<lang>/` (напр. `/ru/` — ru-главная). Это поверхность ЕДИНОЙ
 * ГЛАВНОЙ (start-phase), а НЕ статья Помощи. Возвращает язык (`lang`) если путь —
 * голый лендинг, иначе null (путь несёт slug → это контент-статья). Игровые/реплей/
 * `/en/`-канон уже отсеяны caller'ом (порядок boot) ДО этого вызова.
 */
function landingLangOfPath(pathname: string): UiLang | null {
  const parsed = parseContentPath(pathname);
  return parsed.slug === '' ? parsed.lang : null;
}

/**
 * Смонтировать ЕДИНУЮ ГЛАВНУЮ (start-phase) на заданном языке (fix/i18n-unify-homepage).
 * Глобальный `locale` = язык URL (URL авторитетен для deep-link/Back) → стартовая
 * рендерит секции на нём И рейл-переключатель показывает его (единый источник). Store
 * `setLocale` синхронно (start-copy лежит в бандле — мгновенный рендер на верном языке);
 * каталог игрового UI догружается асинхронно (`applyLocale`, fire-and-forget — лендинг
 * его не ждёт). Заглушка-язык (не CONTENT) → секции на en-фолбэке, URL остаётся `/<lang>/`.
 */
function showLanding(lang: UiLang): void {
  const store = useGameStore.getState();
  if (store.locale !== lang) store.setLocale(lang); // URL-язык → глобальный источник
  store.showStart();
  // Каталог игрового UI на этом языке (рейл/диалоги) — фоном; стартовая-копия из бандла
  // уже отрисована. Idempotent (повтор того же языка — синхронный no-op в runtime).
  void applyLocale(lang, { persist: false });
}

/**
 * Перейти на ЕДИНУЮ ГЛАВНУЮ (start-phase) + записать адресную строку по URL-модели B
 * (en→`/`, ru→`/ru/`) через ЕДИНЫЙ источник `contentPath` (тот же, что контент/SSG) —
 * fix/i18n-unify-homepage. ЕДИНЫЙ ствол для всех переходов на главную, у которых нужно
 * И сменить URL И смонтировать лендинг: §C.3 ru-redirect голого корня, popstate на голый
 * лендинг, смена языка рейлом на стартовой. `replace` — заменить запись истории (boot/
 * редирект), без новой. Маркер `landing` в history-state — popstate отличит главную.
 */
function navigateLanding(lang: UiLang, replace = false): void {
  const path = contentPath(lang, ''); // модель B: en→`/`, ru→`/ru/`
  if (typeof window !== 'undefined' && window.history) {
    const fn = replace ? 'replaceState' : 'pushState';
    window.history[fn]({ landing: { lang } }, '', path);
  }
  showLanding(lang);
}

/**
 * Mount the app into the host element. Idempotent — call once from `main.ts`.
 *
 * Demo-mode routes (used by viewport tests + visual baselines — T-7.3):
 *   - `?demo=teaser` — mount replay-teaser-screen with mock data.
 *   - `?demo=leaderboard` — mount leaderboard-screen with mock rows.
 *   - `?demo=masterjeweler` — leaderboard «Рекорды» on the karat board (Срез 3b).
 * These bypass the normal phase router because the corresponding server
 * endpoints (T-8 leaderboard / T-9 replay) are not yet implemented; the
 * screens themselves are mountable, the demo flag wires mock data in for
 * test/visual coverage.
 */
export function mountApp(host: HTMLElement): void {
  runtime = { host, current: null };

  // Expose the store on window for DevTools / playtest debugging (AC-2 brief:
  // «убедиться через window.__store?.getState().moves в DevTools»).
  exposeStoreToWindow();

  // Прогреть растровые мета-ассеты (звери / 7 камней / монета / осколок): загрузить +
  // декодировать + удержать живые ссылки в памяти — чтобы карточки Мастерской/детали
  // рисовали их мгновенно без повторного декода при пересборке DOM (owner-приоритет
  // «вщёлкивание», meta-gems-resource.md T2 §5). Fire-and-forget — экран не ждёт.
  void preloadMetaAssets();

  // Demo-mode routes (viewport tests). Mount the surface directly; bypass
  // the normal phase router (no Zustand phase for these surfaces yet — they
  // arrive via real server data in T-8 / T-9).
  const params = new URLSearchParams(window.location.search);
  const demo = params.get('demo');
  if (demo === 'teaser') {
    // Тизер теперь i18n-wired (t('game.teaser.*')) — каталог грузим ДО монтажа, чтобы
    // подписи резолвились синхронно (зеркало ?demo=book/?demo=replaysave). Иначе первый
    // кадр = сырые ключи (длиннее RU-литералов → overflow на узких viewport-тестах).
    // Дефолт ru; `&lang=` переопределяет.
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    void setLocale(lang).then(() => {
      useGameStore.getState().setLocale(lang);
      mountDemoTeaser(host);
    });
    return;
  }
  if (demo === 'leaderboard') {
    // Каталог локали грузим ДО монтажа (зеркало ?demo=teaser/book): этот demo-route
    // монтирует экран СИНХРОННО и `return`-ит, минуя re-mount по готовности каталога
    // (bootInitialLocale().then(rerenderActivePhaseForLocale) в main.ts), поэтому без
    // preload первый (и единственный) кадр = сырые ключи `t('game.lb.*')` на ВСЕХ
    // языках вкл. ru. Дефолт ru; `&lang=` переопределяет (как у teaser/book).
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    void setLocale(lang).then(() => {
      useGameStore.getState().setLocale(lang);
      mountDemoLeaderboard(host);
    });
    return;
  }
  if (demo === 'rail') {
    mountDemoRail(host);
    return;
  }
  // Board-figure harness (тикет start-page-redo-and-help AC-19/26): монтирует ОБЩИЙ
  // board-view (static) с пресет-раскладкой по `?fig=<name>` для генерации скриншот-
  // иллюстраций «Помощи» (scripts/gen-help-figures снимает `.board-frame`). Чисто
  // билд-/тест-время, как другие `?demo=` route'ы.
  if (demo === 'boardfig') {
    mountHelpFigureHarness(host);
    return;
  }
  // Sample replay (help-screens-phone fix-cycle 5, owner playtest v6): mounts the REAL
  // replay-screen with a VALID short sample replay (seed + bot moves re-simulated by the
  // canon engine — same source-of-truth as the live player) so the «Помощи» illustration
  // shows the board WITH the replay transport CONTROLS (rewind / play / step / progress).
  // This is the главная претензия of v6: the previous replay illustration showed NO
  // controls. RENDER-only (the screen self-rewinds from seed; no server). `onExit` is a
  // no-op (the harness page has nowhere to navigate). The bot is paused mid-game so the
  // progress bar / board show a representative non-empty state.
  if (demo === 'replaysample') {
    // Каталог локали грузим ДО монтажа (зеркало ?demo=teaser/book): синхронный mount +
    // return минует re-mount по готовности каталога (main.ts) → без preload первый кадр
    // = сырые ключи `t('game.replay')`/`figures.*`. Дефолт ru; `&lang=` переопределяет.
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    void setLocale(lang).then(() => {
      useGameStore.getState().setLocale(lang);
      mountReplaySample(host);
    });
    return;
  }
  // Settings is a real in-shell phase (no server dependency) — the demo route just
  // boots the shell straight into it for deterministic viewport tests + the visual
  // baseline. Normal entry is the nav-rail «Настройки» item (showSettings()).
  if (demo === 'settings') {
    useGameStore.getState().showSettings();
    bootShell(host);
    return;
  }
  // Settings DRAWER (extra-modes §5.5) — boot the shell on the daily hub, then open
  // the settings drawer overlay (for the viewport baseline of the drawer-open state).
  if (demo === 'settingsdrawer') {
    useGameStore.getState().showDaily();
    bootShell(host);
    // Open after the shell mounts (bottom-sheet — no trigger anchoring needed).
    pendingSettingsDrawerOpen?.();
    return;
  }
  // Appearance subscreen (appearance-skins.md §5) — boot the shell on the mode-select
  // hub, then open the settings drawer DIRECTLY on the «Оформление» subscreen (превью
  // + вкладки + сетки свотчей) for the viewport baseline of the appearance state.
  if (demo === 'appearance') {
    useGameStore.getState().showModeSelect();
    bootShell(host);
    pendingAppearanceOpen?.();
    return;
  }
  // Daily-board hub (доска дня) — a real in-shell phase. Boot the shell straight
  // into it for the viewport baseline (idle/done states). Identical store action
  // to the nav-rail «Доска дня» (showDaily()), so the mounted DOM is byte-identical
  // to the real daily hub — this is purely the test-harness entry point. Needed
  // because the default landing is now the «Играть» picker (#13), so `goto('/')`
  // no longer lands on the daily screen. The viewport test mocks /daily/seed.
  if (demo === 'daily') {
    useGameStore.getState().showDaily();
    bootShell(host);
    return;
  }
  // Mode-selector (extra-modes §5.1) — a real in-shell phase with NO server
  // dependency to RENDER (the server is only hit on a card click). Boot the shell
  // straight into it for the viewport baseline.
  if (demo === 'modeselect') {
    useGameStore.getState().showModeSelect();
    bootShell(host);
    return;
  }
  // Start page = ЕДИНАЯ ГЛАВНАЯ-ЛЕНДИНГ (fix/i18n-unify-homepage) — a real in-shell
  // phase with NO server dependency to RENDER (card click hits the server via
  // mode-preview). Boot the shell straight into it for the viewport baseline. Mirror
  // of `?demo=modeselect`. `&lang=ru` СИДИТ начальный ГЛОБАЛЬНЫЙ locale (детерминированный
  // baseline) — стартовая И рейл следуют ему; смена языка рейлом дальше работает штатно
  // (главная перерисовывается, AC «следует рейлу»). startLang() читает store locale.
  if (demo === 'start') {
    const demoLang = params.get('lang');
    if (demoLang && isContentLang(demoLang)) {
      useGameStore.getState().setLocale(demoLang); // baseline-язык → глобальный источник
    }
    useGameStore.getState().showStart();
    bootShell(host);
    return;
  }
  // `?demo=workshop` УДАЛЁН вместе с «животными» (workshop-animals-removal.md): экран
  // зверей снят, мёртвую demo-ветку не держим (AC-7 «не плодить мёртвый UI»).
  // Gnomes plaque (плашка «Гномы» — meta-surface, GN-1) — a real in-shell phase. Boot
  // the shell straight into it for the viewport baseline. The screen renders 7 gnome
  // cards from a placeholder model (server `gnomes` block is GN-4) + reconciles balances
  // from GET /meta/state (the viewport test mocks it). `?state=mix` → demo gnome states
  // (covers all card branches: L1 / L5 / L15 / L20-prestige) for screenshot coverage.
  if (demo === 'gnomes') {
    // companion-focus AC-14: demo-route `?demo=gnomes&focus=<1..7>` для screenshots-DoD/
    // viewport-цикла. Фокус-цель ОБЯЗАНА пережить boot-strip → парсим `focus` ПЕРЕД
    // mount и кладём в store-payload через showGnomes(focusColor) (тот же путь, что
    // реальный тап оверлея). Экран потребит её one-shot (AC-3) — поэтому даже на demo-
    // route фокус не «прилипает» к re-mount (смена языка). Невалидный focus → undefined.
    const focusParam = Number(params.get('focus'));
    const focusColor =
      Number.isInteger(focusParam) && focusParam >= 1 && focusParam <= 7
        ? (focusParam as ColorIndex)
        : undefined;
    useGameStore.getState().showGnomes(focusColor);
    bootShell(host);
    return;
  }
  // Helper-unlock overlay (оверлей «Открыт новый помощник!» — тикет helper-unlock-overlay-
  // redesign 2026-06-20) — boots the shell on the «Помощники» screen, then mounts the reward
  // overlay over it with a mock server-truth unlock. Lets the owner verify the overlay card
  // (1-в-1 с карточкой «Помощники») WITHOUT replaying a game; also the viewport baseline.
  // `?color=N` (1..7) picks the gnome (default 3 = зелёный, как в comparison-скринах);
  // `&reduced=1` показывает СРАЗУ финальную (settled) карточку без reveal-вспышки.
  if (demo === 'helperunlock') {
    useGameStore.getState().showGnomes();
    bootShell(host);
    const colorParam = Number(params.get('color'));
    const color = Number.isInteger(colorParam) && colorParam >= 1 && colorParam <= 7 ? colorParam : 3;
    const reduced = params.get('reduced') === '1';
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    // Дождаться lazy-каталога локали (иначе табличка «Новый помощник» мелькнёт сырым ключом
    // до загрузки JSON — в реальном потоке конца партии каталог уже загружен).
    void setLocale(lang).then(() => {
      mountHelperUnlockOverlay({
        summary: {
          progression: { gnome_unlocks: [{ color, milestone_index: 1, milestone_level: 6 }] },
        } as unknown as Parameters<typeof mountHelperUnlockOverlay>[0]['summary'],
        reduceMotion: reduced,
      });
    });
    return;
  }
  // Profile (хаб-Профиль — meta-surface, Срез 4b) — a real in-shell phase. Boot the
  // shell straight into it for the viewport baseline. The screen self-fetches
  // GET /meta/state (+ progression); the viewport test mocks it.
  if (demo === 'profile') {
    useGameStore.getState().showProfile();
    bootShell(host);
    return;
  }
  // «Книга реплеев» (личный каталог реплеев — replay-collection.md / ADR-0023) — реальная
  // in-shell фаза. Boot the shell straight into it (рейл присутствует — нужен rail-clearance
  // viewport-тесту). Экран self-fetch'ит GET /book/* (viewport-тест мокает эндпоинты);
  // `&album=own|others` задаёт стартовый альбом (читается в mountPhase 'book'). empty/orphan/
  // guest приходят из мок-ОТВЕТОВ сервера (пустые массивы / orphan-строка / 401) — боевой путь.
  if (demo === 'book') {
    // Дефолт-локаль демо = ru (owner-lock копи `book.*` RU-первичен; viewport-тесты
    // ждут RU-подписи). `&lang=` переопределяет. Каталог грузим ДО монтажа (t() синхронно).
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    // Детерминированные данные Книги для демо/viewport-тестов: ставим fetch-перехват
    // `/book/*` ДО монтажа (зеркало того, как `?demo=daily` мокает /daily/seed на
    // стороне теста, но здесь — на стороне app, т.к. narrow-landscape viewport-тест
    // мокает только /auth/anonymous). Параметры (единый контракт демо-Книги):
    //   `&long=1`       → длинный список «Своих» (≥20 строк, bounded-scroll, AC-2a-6);
    //   `&album=others` → счётчик used/capacity + CTA «Расширить ёмкость»;
    //   `&slice=empty`  → пустой срез (empty-state, AC-A9);
    //   `&guest=1`      → гость (401 → CTA логина, AC-A12);
    //   `&row=orphan`   → «Чужие»: одна осиротевшая строка (available=false, AC-A8);
    //   `&guestcap=1`   → /book/own отдаёт guest_capped=true (upsell-объяснение капа
    //                     гостя ПОД списком «Свои», owner G-2 2026-06-24).
    // Все прочие запросы (incl. /auth/anonymous) проходят к исходному fetch. Демо-only.
    installBookDemoFetch({
      long: params.get('long') === '1',
      slice: params.get('slice') === 'empty' ? 'empty' : 'filled',
      guest: params.get('guest') === '1',
      orphan: params.get('row') === 'orphan',
      guestCapped: params.get('guestcap') === '1',
    });
    void setLocale(lang).then(() => {
      useGameStore.getState().setLocale(lang);
      useGameStore.getState().showBook();
      bootShell(host);
    });
    return;
  }
  // Кнопка-дискета «Сохранить в Книгу» на просмотре ЧУЖОГО реплея (тикет B) — монтирует
  // РЕАЛЬНЫЙ replay-screen с детерминированной сэмпл-партией + (для `kind=foreign`) общий
  // kit дискеты (lib/replay-save-button). RENDER-only (self-rewind from seed; book-API
  // мокается viewport-тестом). Параметры: `&kind=foreign|own` (дискета есть/нет),
  // `&state=save|saved` (не сохранено / в Книге), `&full=1` (альбом 5/5 → 409 овершлоу при
  // клике), `&guest=1` (клик → логин), `&surface=teaser` (тизер чужой daily ВМЕСТО плеера —
  // AC-B7; та же дискета в шапке тизера, без доски). Зеркало `?demo=replaysample`.
  if (demo === 'replaysave') {
    // Дефолт-локаль ru (owner-lock `book.overflow` = «Нет места в книге»; тест ждёт RU).
    // Каталог грузим ДО монтажа, чтобы попап-текст резолвился синхронно на клике.
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    void setLocale(lang).then(() => {
      useGameStore.getState().setLocale(lang);
      mountDemoReplaySave(host, params);
    });
    return;
  }
  // Auth-modal (поверхность входа — account-profile-redesign.md §4) — boots the shell
  // on the mode-select picker, then opens the auth-modal overlay for the viewport
  // baseline (4 classes: header «Вход» + ✕ + 3 OAuth + «Войти по почте»). RENDER-only:
  // OAuth-кнопки уводят на провайдера (test asserts the rendered dialog before clicks).
  // Mirrors `?demo=revokedbanner` (open an overlay over a booted shell).
  if (demo === 'authmodal') {
    useGameStore.getState().showModeSelect();
    bootShell(host);
    // Каталог локали грузим ДО открытия модалки: overlay читает `t('auth.panel.*')`
    // ОДИН раз при open и НЕ ре-рендерится на смену locale (в отличие от in-shell фазы,
    // что перемонтируется по готовности каталога). Открываем ПОСЛЕ preload, иначе
    // первый кадр = сырые ключи. Дефолт ru; `&lang=` переопределяет.
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    void setLocale(lang).then(() => {
      useGameStore.getState().setLocale(lang);
      requestAnimationFrame(() => openLoginFlow());
    });
    return;
  }
  // All-time leaderboard variant (extra-modes §5.4) — boots the shell straight into
  // the all-time leaderboard on the predict mode tab. The screen self-fetches
  // `/alltime/leaderboard?mode=` (the viewport test mocks it).
  if (demo === 'alltime') {
    useGameStore.getState().showLeaderboard('predict');
    bootShell(host);
    return;
  }
  // Revoked-session banner (incident 2026-06-10) — boots the shell on the daily hub,
  // then shows the «Сессия завершена» banner for the viewport baseline (4 classes).
  // RENDER-only: the banner's reload action is harmless in the test (it asserts the
  // rendered dialog before any click). Mirrors `?demo=settingsdrawer`.
  if (demo === 'revokedbanner') {
    useGameStore.getState().showDaily();
    bootShell(host);
    void showSessionRevokedBanner();
    return;
  }
  // Pre-start PREVIEW (run-persistence-lifecycle.md T-A, lazy-on-preview) — mounts the
  // non-daily game screen in preview of `predict` (the score/moves line morphs into
  // «Начать» + «перебросить старт»). RENDER-only: seeds a demo all_time session with
  // `preStart=true` (no real `POST /game/start` — the production path allocates it via
  // `lib/mode-preview`). For the viewport baseline of the pre-start preview.
  if (demo === 'lineselect') {
    useGameStore.getState().startAllTimeGame({
      seed: 12345,
      sessionId: 'demo-session',
      mode: 'predict',
      preStart: true,
    });
    bootShell(host);
    return;
  }
  // Non-daily game PLAYING state (extra-modes §5.2 — post-«Начать») — seed a started
  // all-time game (the line-selector reverts to score/moves). RENDER-only.
  if (demo === 'board-alltime') {
    useGameStore.getState().startAllTimeGame({ seed: 12345, sessionId: 'demo-session', mode: 'predict' });
    bootShell(host);
    return;
  }
  // Non-daily result screen (extra-modes §5.3) — seed a finished classic game +
  // a verified verdict, then show the result phase. RENDER-only (no submit fires;
  // the verdict is pre-set). For the viewport baseline of the non-daily result.
  if (demo === 'result-alltime') {
    const store = useGameStore.getState();
    store.startAllTimeGame({ seed: 12345, sessionId: 'demo-session', mode: 'classic' });
    useGameStore.getState().endGame('player_finish');
    useGameStore.getState().setDailySubmitResult({
      status: 'verified',
      gameId: 'demo',
      scoreVerified: 0,
      linesVerified: 0,
      replayUrl: '/r/demoalltime1',
    });
    useGameStore.getState().showResult();
    bootShell(host);
    return;
  }
  // ⚠ TEMP dev-роут (owner-запрос 2026-06-22 «ссылка как у гнома», тикет fe-achievement-
  // result-window AC-DEMO) — `?demo=achievementwindow`: грузит экран итогов над доской с
  // МОК server-truth ачивками + монтирует окошко ачивок (зеркало `?demo=result-alltime`,
  // без реальной партии). Параметры: `&count=N` (сколько ачивок, default 3; большое N →
  // overflow «+ ещё N» bounded-к-доске), `&reduced=1` (мгновенно, reduce-motion),
  // `&lang=ru|en` (дождаться lazy-каталога перед монтажом, иначе сырой ключ мелькнёт). В
  // ДЕМО окошко PERSISTENT (auto-dismiss выкл — флаг isDemoAchievementWindow в dev-flags →
  // result-screen передаёт autoDismiss:false); реальный поток сохраняет авто-уход 3000ms.
  // Удалить вместе с прочими `?demo=*` перед продом. Мок-ачивки = РЕАЛЬНЫЕ id из каталога
  // (ACHIEVEMENT_DISPLAY), награда выводится окошком из achievementReward (НЕ выдуманы).
  if (demo === 'achievementwindow') {
    const countParam = Number(params.get('count'));
    const count =
      Number.isInteger(countParam) && countParam > 0
        ? Math.min(countParam, ACHIEVEMENT_DISPLAY.length)
        : 3;
    const demoLang = params.get('lang');
    const lang: UiLang = demoLang && isContentLang(demoLang) ? demoLang : 'ru';
    const achievements_unlocked = ACHIEVEMENT_DISPLAY.slice(0, count).map((d) => ({
      id: d.id,
      tier_index: 0,
    }));
    const store = useGameStore.getState();
    store.startAllTimeGame({ seed: 12345, sessionId: 'demo-session', mode: 'classic' });
    useGameStore.getState().endGame('player_finish');
    useGameStore.getState().setDailySubmitResult({
      status: 'verified',
      gameId: 'demo',
      scoreVerified: 0,
      linesVerified: 0,
      replayUrl: '/r/demoachwin1',
      meta: {
        gold_earned: 50,
        shards_earned: 6,
        gems_dropped: 0,
        gem_pool_delta: [0, 0, 0, 0, 0, 0, 0, 0],
        progression: {
          xp_earned: 30,
          level_before: 10,
          level_after: 10,
          levels_gained: 0,
          milestone_rewards: [],
          achievements_unlocked,
          gnome_unlocks: [],
        },
      },
    });
    // Дождаться lazy-каталога локали (как helperunlock) — иначе подписи мелькнут сырым
    // ключом до загрузки JSON. После — showResult монтирует окошко (result-screen ветка).
    void setLocale(lang).then(() => {
      useGameStore.getState().showResult();
      bootShell(host);
    });
    return;
  }

  // ⚠ TEMP dev (owner 2026-05-30): `?dev=resetdaily` — чистит КЛИЕНТСКИЙ кэш доски
  // дня (IndexedDB/localStorage: played + in_progress), чтобы снова зайти в превью/
  // заход (состояние «уже сыграно» живёт в браузере, не на сервере). После очистки —
  // редирект на чистый `/`. Удалить вместе с `?dev=daily`.
  if (new URLSearchParams(window.location.search).get('dev') === 'resetdaily') {
    void Promise.all([clearPlayed(), clearInProgress()]).finally(() => {
      window.location.replace('/');
    });
    return;
  }

  // ⚠ TEMP dev (owner 2026-05-30): `?dev=daily` — запускаем СВЕЖИЙ заход доски дня
  // локально (обход «одна попытка в день»), чтобы тестировать заход→result. Сабмит
  // на сервер пропускается в finalizeGame (board-screen) → mock-вердикт «Значтено».
  if (isDevDaily()) {
    useGameStore.getState().startDailyGame({
      seed: Math.floor(Math.random() * UINT32_MAX) >>> 0,
      dateOfDay: dateOfDayFromUtc(Date.now()),
    });
    bootShell(host);
    return;
  }

  // `?demo=dailygame` — ДЕТЕРМИНИРОВАННЫЙ игровой экран доски дня для screenshot-baseline
  // (П4 timer DoD §0.D): ФИКС seed + ФИКС dateOfDay → доска воспроизводима, PNG не флакает
  // (аналог `?dev=daily`, но без random seed). Только build-/тест-время, как прочие demo.
  if (new URLSearchParams(window.location.search).get('demo') === 'dailygame') {
    useGameStore.getState().startDailyGame({
      seed: 305419896, // фикс seed (0x12345678) → детерминированная раздача шаров
      dateOfDay: '2026-06-22',
    });
    bootShell(host);
    return;
  }

  // Deep-link replay route `/r/<short_id>` (replays-ghosts.md §5.4). Boot the
  // shell, then drive the `replay` phase with the short_id — the phase mount
  // fetches GET /api/replay/<short_id> and branches player/teaser/not-found
  // STRICTLY per the server `kind` (the client never decides the gate). The seed
  // is server-authoritative inside the playable payload (no ?seed= leak).
  const replayShortId = parseReplayShortIdFromPath();
  if (replayShortId) {
    useGameStore.getState().showReplayById(replayShortId);
    bootShell(host);
    return;
  }

  const { phase: initialPhase, querySeed } = decideInitialPhase();
  if (initialPhase === 'board') {
    // Casual entry (`?seed=` / `?mode=casual`) — start engine immediately so board
    // mount has state. Highest priority (dev/QA reproducibility) — overrides path.
    useGameStore.getState().startNewGame(querySeed ?? undefined);
    bootShell(host);
    return;
  }

  // --- Per-route DEEP-LINK boot — URL-МОДЕЛЬ B (ADR-0021 addendum-001 §A/§C) -------
  // The address bar is the single source of the initial surface. ПОРЯДОК КРИТИЧЕН под
  // моделью B (en = корень, поэтому контентный парсер матчит ЛЮБОЙ путь):
  //   реплей → 301-канон `/en/` → ГОЛЫЙ ЛЕНДИНГ (`/`,`/ru/`) → ИГРОВОЙ путь → контент.
  // Контент-СТАТЬЯ проверяется ПОСЛЕДНЕЙ (иначе `/play` распарсился бы как en-slug).
  // Все пути origin-relative (localhost / LAN); боевой домен — только в краулер-мета.
  const path = window.location.pathname;

  // 0) Канонизация `/en/...` → корне-вариант (301, ADR-0021 addendum-001 §A): английский
  //    живёт ТОЛЬКО в корне. `/en/` → `/` (главная), `/en/rules/` → `/rules/` (статья).
  //    В dev — replaceState (нет HTTP-кодов); прод-301 отдаёт Caddy (deploy-док). После
  //    канонизации цель — либо голый лендинг (slug='') → главная, либо статья → контент.
  const enCanon = canonicalEnRedirect(path);
  if (enCanon !== null) {
    const landLang = landingLangOfPath(enCanon);
    if (landLang !== null) {
      // `/en/` → `/` главная (start-phase, en). URL фиксирует app.ts boot-replaceState ниже.
      showLanding(landLang);
    } else {
      const parsed = parseContentPath(enCanon);
      navigateContent(parsed.lang, parsed.slug, /* replace */ true); // статья → SPA-переход
    }
    bootShell(host);
    return;
  }

  // 1) ГОЛЫЙ ЛЕНДИНГ-путь (`/` en-главная ИЛИ `/<lang>/` без slug, напр. `/ru/`) — ЕДИНАЯ
  //    ИНДЕКСИРУЕМАЯ ГЛАВНАЯ (fix/i18n-unify-homepage). Проверяем ДО игрового
  //    классификатора (главная — не игровой route). Голый корень `/` (en) дополнительно
  //    учитывает §C.3: нет явного персиста И язык браузера = индексируемый не-en (`ru`) →
  //    редирект (replaceState dev / 302 прод-Caddy) на `/ru/`. Иначе монтируем главную на
  //    языке URL (showLanding сетит глобальный locale → стартовая + рейл следуют URL).
  const landingLang = landingLangOfPath(path);
  if (landingLang !== null) {
    if (isSiteRoot(path)) {
      const redirectTo = resolveRootRedirectSync();
      if (redirectTo) {
        // ru-браузер без персиста → главная на `/ru/` (replaceState + ru-главная).
        navigateLanding(redirectTo, /* replace */ true);
        bootShell(host);
        return;
      }
    }
    showLanding(landingLang); // `/`→en-главная, `/ru/`→ru-главная (язык URL авторитетен)
    bootShell(host);
    return;
  }

  // 1b) LEGACY app-адрес → 301-канон на новый URL (shared `legacyAppRouteRedirect`).
  //     `/gnomes` → `/companions` (owner-decision 2026-06-21: переименование экрана
  //     «Помощников» в Companions на уровне URL + видимой en-подписи; внутренний id
  //     `gnomes` НЕ тронут). Старый адрес НЕ 404-ит и НЕ монтирует пустоту — переезжает
  //     на новый. В dev — `replaceState` (нет HTTP-кодов); прод-301 отдаёт Caddy
  //     (deploy-док). Проверяется ДО игрового классификатора (после канонизации путь
  //     уже новый, classifyGamePath его опознает как живое направление).
  const legacyTarget = legacyAppRouteRedirect(path);
  const routePath = legacyTarget ?? path;
  if (legacyTarget !== null && typeof window !== 'undefined') {
    window.history.replaceState({ game: legacyTarget }, '', legacyTarget);
  }

  // 2) Игровой путь (`/play`, `/daily`, `/leaderboard`, `/workshop`, `/companions`,
  //    `/profile`) → соответствующая фаза — ДО контента (модель B: контент матчит всё).
  //    `/daily` → ХАБ (I-2 anti-shadow: ритуал + отсчёт + CTA, НЕ сегодняшняя раздача).
  const gameRoute = classifyGamePath(routePath);
  if (gameRoute) {
    applyGameRoute(gameRoute);
    bootShell(host);
    // `/play` deep-link — как дефолт: восстановить активную партию, если есть в кэше.
    if (gameRoute === 'play') void resumeOrLobby();
    return;
  }

  // 2b) RETIRED игровой путь (`/workshop` — мастерская «животных» снята,
  //     workshop-animals-removal.md O-3 / fe-workshop-disabled AC-6): graceful fallback
  //     на дефолт-поверхность (пикер), НЕ монтаж экрана зверей, НЕ контент-404, НЕ
  //     белый экран. Адрес выправляем на `/play` (replaceState) — не оставляем мёртвый
  //     `/workshop` в строке. Проверяется ДО контент-классификатора (иначе `/workshop`
  //     уехал бы в content-screen как несуществующий slug).
  if (isRetiredGamePath(path)) {
    useGameStore.getState().showModeSelect();
    if (typeof window !== 'undefined') {
      window.history.replaceState({ game: 'play' }, '', GAME_ROUTE_PATHS.play);
    }
    bootShell(host);
    void resumeOrLobby();
    return;
  }

  // 3) Контент-СТАТЬЯ (модель B): путь со slug — страница Помощи (`/rules`, `/ru/faq`,
  //    `/de/x`-заглушка). showContent — тот же контент, что видит краулер. Голый лендинг
  //    сюда НЕ попадает (отсечён шагом 1) — content-screen рендерит только статьи.
  const contentBoot = classifyBootPath(path);
  if (contentBoot.kind === 'content') {
    useGameStore.getState().showContent(contentBoot.lang, contentBoot.slug);
    bootShell(host);
    return;
  }

  // 4) Защитный дефолт (теоретически недостижим — классификатор покрывает всё): пикер.
  useGameStore.getState().showModeSelect();
  bootShell(host);

  // T-B / AC-B.4: if an active unfinished non-daily run is cached (e.g. the player
  // refreshed mid-game — the Zustand store is not persisted, so boot landed on the
  // lobby), RESUME it directly into the board instead of stranding the player on the
  // lobby. `resumeOrLobby` flips to `board` (re-mounting via the phase subscription)
  // when a run exists; otherwise it's a no-op (we already showed the lobby, so the
  // daily prewarm rationale still holds for the no-run case). Async (cache read) —
  // fire-and-forget after the shell is up.
  void resumeOrLobby();
}

/**
 * Parse a `/r/<short_id>` deep-link path. Returns the short_id, or null if the
 * pathname isn't a replay route. The short_id shape mirrors the server schema
 * (base62, 8–32 chars; ADR-0003) — a malformed segment is treated as no-match
 * (falls through to the normal boot rather than fetching a guaranteed-404).
 */
function parseReplayShortIdFromPath(): string | null {
  if (typeof window === 'undefined') return null;
  const match = /^\/r\/([0-9A-Za-z]{8,32})\/?$/.exec(window.location.pathname);
  return match ? match[1]! : null;
}

/**
 * Boot the persistent app-shell: mount the global nav-rail ONCE into `host`,
 * then drive screen phases into its content slot (the rail is never recreated —
 * navigation lives across screen swaps). The rail's active item tracks the phase.
 *
 * Nav destinations (real store actions, in-shell — no page reload):
 *   - Играть    → startNewGame() (casual, phase `board`)
 *   - Доска дня → showDaily()    (phase `daily`)
 *   - Лидерборд → showLeaderboard() (phase `leaderboard`; mock data until T-8)
 *   - Профиль → showProfile() (Мастерская — disabled-пункт, не направление)
 *
 * Account-slot (account-profile-redesign.md §2): state-driven слот рейла — гость →
 * пилюля «Войти» (→ auth-modal через `openLoginFlow`); залогинен → account-chip (меню
 * «Выйти»; «Профиль» — отдельный пункт nav-list). Reconcile на любую смену identity —
 * подписка на `onIdentityChanged`.
 */
function bootShell(host: HTMLElement): void {
  // Settings DRAWER (extra-modes §5.5, owner-decision 2026-06-04) — singleton overlay,
  // mounted once at the shell level. Opens from the bottom-rail GEAR UTILITY (NOT a
  // nav destination) over ANY surface (board during a game / daily / menu) WITHOUT
  // changing the phase → the game is not reset (AC-17 / §6.12). The gear does NOT go
  // through beforeNavigate (leave-guard) → во время партии ничего не спрашивает.
  const settingsDrawer = mountSettingsDrawer();

  const navRail: NavRailHandle = mountNavRail(host, {
    items: realNavItems(),
    // Бренд-логотип слева-вверху кликабелен → СТАРТОВАЯ (owner-decision 2026-06-17:
    // клик логотипа → стартовая `/`, паттерн Minesweeper Online). Раньше вёл на дефолт-
    // лендинг-контент; теперь главная = минимальный лендинг (start phase). Через
    // store.showStart() — URL синхронизируется в syncUrl (pathForPhase('start')==='/').
    onBrandSelect: () => useGameStore.getState().showStart(),
    // Account-slot (account-profile-redesign.md §2): стартовая идентичность из identity
    // (синхронно, instant-hydration §2.3) + колбэки входа/профиля/выхода.
    account: navRailAccountIdentity(),
    accountCallbacks: {
      // login-пилюля (гость) → auth-modal (§2.1, единый auth-flow). Reconcile slot —
      // через подписку на identity-events ниже (один канал, не дубль).
      onLogin: () => openLoginFlow(),
      // Меню chip «Выйти» (§3.1) — ОДНА общая logout-функция (lib/auth-flow, не копия).
      // Пункт профиля — отдельный пункт nav-list (свой onSelect через leave-guard), не чип.
      onLogout: () => authLogout(),
    },
    // Шестерёнка settings — УТИЛИТА внизу рейла (иконка без подписи), НЕ
    // направление: открывает drawer оверлеем (фаза не меняется → партия цела).
    utilities: [
      {
        id: 'settings',
        icon: 'settings',
        // T-I18N-RAIL: aria/title из каталога (reuse game.settings), локализуется (AC-5).
        ariaLabelKey: 'game.settings',
        onSelect: () => settingsDrawer.open(),
      },
    ],
    // T-C (RP-6): навигация по рейлу из запущенной игры — БЕЗ confirm (тихое
    // сохранение). Прогресс уже в кэше (T-B), возврат через play-пункт восстанавливает
    // партию → confirm на навигации снят (раньше beforeNavigate → confirmAbandon).
  });

  // Плашка «сессия завершена» (revoked-session, incident 2026-06-10) — singleton,
  // подписана один раз на уровне shell. api-client публикует revoked-сигнал при
  // 401 token_not_recognized на АВТОРИЗОВАННОЙ identity (вход с другого браузера),
  // вместо прежнего тихого re-mint'а призрачного гостя. Плашка переиспользует
  // примитив confirm-dialog (lib/session-revoked-banner).
  installSessionRevokedBanner();

  // Кросс-вкладочный мост revoked-сигнала (owner-bug мультитап 2026-06-22) —
  // singleton shell-подписка. Первая вкладка, узнавшая о revoke (api-client 401 /
  // heartbeat), оповещает остальные вкладки той же origin через BroadcastChannel
  // (fallback storage-event); каждая зовёт emitSessionRevoked() локально и СРАЗУ
  // поднимает ту же плашку, не дожидаясь focus/собственного запроса. Реюз seam
  // (session-revoked) + плашки (session-revoked-banner) — мост лишь транспорт, без
  // DOM. Гард isAuthed (как heartbeat) — гостю re-mint не триггерит.
  installSessionRevokedBroadcast();

  // Liveness-heartbeat (owner-bug 2026-06-11) — screen-agnostic shell-подписка:
  // на возврат вкладки/окна в фокус для АВТОРИЗОВАННОЙ identity шлёт дешёвый
  // авторизованный GET (`getMetaState`), чтобы «прощупать» отозванный токен и
  // поднять плашку выше, даже если текущий экран (напр. доска дня) шлёт лишь
  // ПУБЛИЧНЫЕ запросы (без Bearer → не 401 → отзыв не детектится → серая доска
  // без плашки). ОТДЕЛЬНАЯ подписка — не энтанглит focus-хендлеры экранов
  // (leaderboard refresh / daily rollover). Живёт весь lifetime shell (как
  // beforeunload ниже) — teardown возвращён для симметрии/тестируемости.
  installSessionHeartbeat();

  // Account-slot реактивность (§2.3): ОДИН канал reconcile — на любую смену identity
  // (вход из пилюли/баннера Профиля, выход из меню chip) рейл-slot перерисовывается
  // из свежей identity. Подписка живёт весь lifetime shell (рейл — singleton).
  onIdentityChanged(() => {
    navRail.setAccountState(navRailAccountIdentity());
    // Sync-при-логине языка (ADR-0021 D-4a): вошёл в аккаунт В СЕССИИ (не только на
    // boot) → слить выбор языка «последний явный человека побеждает» (гостевой
    // localStorage → на аккаунт; иначе серверный → применить). Тот же ствол, что
    // boot (lib/i18n/syncLocaleOnLogin). Аноним/выход — no-op внутри (isAuthed).
    void syncLocaleOnLogin();
  });

  // helper-unlock A2 (AC-9/11/12/13): бейдж-точка «есть новые помощники» на пункте «Помощники».
  // ОДИН канал: на каждый свежий server-truth granted (общий seam meta-state-events — эмитит
  // boot-префетч / mount мета-экрана / ресинк) сравниваем granted с per-player last-seen
  // (lib/gnome-unlock-badge) и ставим/снимаем dot через рейл-handle (singleton не пересоздаём,
  // как setActive). Бейдж — чистый UX-индикатор (display-only, I-Gn1): на мету/добычу не
  // влияет. Локализованная aria-добавка «есть новые помощники» (смысл не только цветом, AC-13).
  const refreshGnomesBadge = (granted: number): void => {
    const unseen = hasUnseenUnlocks(granted, loadLastSeenUnlocks());
    navRail.setItemBadge('gnomes', unseen ? 'dot' : null, t('game.rail.gnomesNewAria'));
  };
  onMetaStateGranted(refreshGnomesBadge);

  // fe-achievement-result-window AC-8: точка «есть новые достижения» на пункте «Профиль».
  // Тот же seam (onMetaStateGranted эмитится на любой свежий server-truth меты + при сбросе
  // из profile-screen), но точка ачивок питается open-set `progression.achievementIds` снимка
  // (server-truth, его читает Профиль — резолюция оркестратора п.1: БЕЗ нового серверного
  // поля) против per-player seen-set (lib/achievement-seen-badge). Дисплей-онли (на мету не
  // влияет). Локализованная aria-добавка «есть новые достижения» (смысл не только цветом).
  const refreshProfileBadge = (): void => {
    const open = loadMetaSnapshot()?.progression.achievementIds ?? [];
    const unseen = hasUnseenAchievements(open, loadSeenAchievements());
    navRail.setItemBadge('profile', unseen ? 'dot' : null, t('game.rail.profileNewAchAria'));
  };
  onMetaStateGranted(refreshProfileBadge);

  // Смена языка: перелокализовать aria-добавку активных бейджей (granted/open читаем из снимка —
  // server-truth уже легло; если снимка нет — пусто снимет бейдж до следующего ресинка).
  useGameStore.subscribe((s, prev) => {
    if (s.locale !== prev.locale) {
      refreshGnomesBadge(loadMetaSnapshot()?.gnomeUnlocksGranted ?? 0);
      refreshProfileBadge();
    }
  });

  // После reload по выбору «Войти снова» (revoked-session §4.1) — авто-открыть ТУ ЖЕ
  // auth-modal (one-shot sessionStorage-флаг). Раньше кликали по `.nav-item--login`
  // (rail-попап провайдеров); теперь поверхность входа едина — auth-modal (auth-flow).
  if (consumeOpenLoginFlag()) {
    requestAnimationFrame(() => {
      openLoginFlow();
    });
  }

  // T-C (RP-6): закрытие / перезагрузка вкладки — БЕЗ нативного prompt. Прогресс
  // тихо сохраняется в кэш (T-B) после каждого хода; возврат через «Играть»
  // восстанавливает партию. Раньше тут вешался beforeunload-guard (нативный prompt
  // при незавершённой игре) — снят (нечего терять, «случайно закрыть» невозможно).
  // Screens render into the rail's content slot (full-width — board centres there,
  // the rail floats over the empty left margin and never shrinks the board).
  runtime = { host: navRail.contentSlot, current: null };

  const syncActive = (): void => {
    const s = useGameStore.getState();
    let active: string | null = null;
    if (s.phase === 'leaderboard') active = 'leaderboard';
    else if (s.phase === 'daily') active = 'daily';
    else if (s.phase === 'settings') active = 'settings';
    // 'workshop'-фазы больше нет (workshop-animals-removal.md): пункт disabled, не
    // получает active-highlight (нет «кнопки-призрака», AC-7).
    else if (s.phase === 'gnomes') active = 'gnomes';
    else if (s.phase === 'profile') active = 'profile';
    else if (s.phase === 'content') active = 'content';
    else if (s.phase === 'modeSelect') active = 'play';
    else if (s.phase === 'board') active = s.context.kind === 'daily' ? 'daily' : 'play';
    // result / replay — no active highlight (transient post-game screens).
    navRail.setActive(active);
  };
  // Settings — это оверлей-утилита (шестерёнка), НЕ раздел: подсветки нет. Колбэк
  // для demo-маршрута `?demo=settingsdrawer` (открыть drawer при boot).
  pendingSettingsDrawerOpen = () => settingsDrawer.open();
  pendingAppearanceOpen = () => settingsDrawer.openAppearance();

  // --- Per-route URL sync (ADR-0020 Фаза 3) -------------------------------------
  // ОДИН источник pushState для ИГРОВЫХ фаз: на смену фазы → синхронизируем адресную
  // строку с `pathForPhase` (lib/game-routing — единый словарь phase↔path). Контент-
  // фаза управляет СВОИМ URL сама (navigateContent → pushState ДО showContent;
  // pathForPhase('content') === null → здесь не трогаем → нет двойного push). Replay
  // deep-link `/r/<id>` — свой server-authoritative путь, фаза `replay` не пушит.
  //
  // `suppressUrlSync` — guard от цикла: popstate (Back/Forward) восстанавливает фазу,
  // её phase-change подписка НЕ должна снова pushState (иначе сломаем историю). Флаг
  // ставится на время применения popstate-навигации.
  let suppressUrlSync = false;
  const syncUrl = (phase: Phase): void => {
    if (suppressUrlSync) return;
    const want = pathForPhase(phase);
    if (want === null) return; // транзиентная / контент-фаза — адрес не трогаем.
    if (window.location.pathname.replace(/\/+$/, '') === want.replace(/\/+$/, '')) {
      return; // уже на этом пути (boot deep-link / повторный клик) — без лишней записи.
    }
    window.history.pushState({ game: want }, '', want);
  };

  useGameStore.subscribe((s, prev) => {
    const phaseChanged = s.phase !== prev.phase;
    // Контент-фаза: переход МЕЖДУ разделами (меню разделов / крошки / lang-switcher)
    // НЕ меняет phase ('content'→'content') — меняется только contentTarget. Без этой
    // ветки content-screen не перемонтируется и текст статьи остаётся старым
    // (owner-playtest 2026-06-17: «по нажатию кнопок текст не меняется»). Тот же фикс
    // чинит Назад/Вперёд между контент-страницами (popstate → showContent → та же фаза).
    const contentChanged =
      s.phase === 'content' &&
      (s.contentTarget?.lang !== prev.contentTarget?.lang ||
        s.contentTarget?.slug !== prev.contentTarget?.slug);
    // СТАРТОВАЯ-ЛЕНДИНГ: смена ЯЗЫКА рейлом НЕ меняет phase ('start'→'start') — меняется
    // только `locale`. Без этой ветки главная не перерисовалась бы на новом языке и URL
    // не переехал бы по модели B (en→`/`, ru→`/ru/`). ЕДИНАЯ ИНДЕКСИРУЕМАЯ ГЛАВНАЯ
    // СЛЕДУЕТ РЕЙЛУ (fix/i18n-unify-homepage, ADR-0021 addendum-001 §D). Аналог
    // contentChanged для контента — тот же приём «re-mount + URL на смену в той же фазе».
    const startLangChanged = s.phase === 'start' && s.locale !== prev.locale;
    // СМЕНА ЯЗЫКА на любой ВНУТРИ-ОБОЛОЧЕЧНОЙ ИГРОВОЙ фазе (daily / modeSelect / board /
    // result / replay / leaderboard / settings / gnomes / profile / book) — НЕ меняет
    // phase, меняется только `locale`. Эти экраны рендерят подписи через `t()` ОДИН раз
    // при mount и НЕ подписаны на locale сами → без re-mount остаются на ПРОТУХШЕМ языке
    // (owner-bug 2026-06-24: «Архив игр» слетает при смене языка; чинило лишь ре-навигация
    // по рейлу = ре-mount). Общий механизм (НЕ костыль под один book): ре-монтируем
    // АКТИВНУЮ игровую фазу — тот же приём re-mount, что уже у start (startLangChanged) и
    // контента (contentChanged). start/content ИСКЛЮЧЕНЫ (у них своя ветка + свой URL-
    // эффект) — иначе двойной mount. Тот же re-mount чинит F5/deep-link-гонку с lazy-
    // каталогом: на boot фаза монтируется СИНХРОННО ДО готовности каталога (main.ts:
    // applyLocale fire-and-forget, mountApp синхронно) → первый кадр на сырых ключах;
    // когда каталог дозагрузился, applyLocale делает store.setLocale → эта ветка
    // ре-монтирует активную фазу уже на верном языке (лейблы + список рисуются заново).
    const gamePhaseLangChanged =
      s.locale !== prev.locale && s.phase !== 'start' && s.phase !== 'content';
    if (phaseChanged || contentChanged || startLangChanged || gamePhaseLangChanged) {
      // force-перемонтаж при смене раздела (контент) ИЛИ языка (главная / игровая фаза).
      mountPhase(s.phase, contentChanged || startLangChanged || gamePhaseLangChanged);
      syncActive();
      if (phaseChanged) {
        syncUrl(s.phase); // контент свой URL пушит сам (navigateContent)
      } else if (startLangChanged) {
        syncUrl(s.phase); // главная: URL по модели B от locale (pathForPhase('start'))
      }
      // SPA noindex-страховка (SEO-хардинг 2026-06-19): после синхронизации URL
      // выставить/снять `<meta robots noindex>` по ТЕКУЩЕМУ пути — игровой app-путь →
      // noindex, контент/лендинг → снять. Читаем location.pathname ПОСЛЕ syncUrl/
      // navigateContent (URL уже актуален). Единый предикат с robots.txt (app-routes).
      syncRobotsMeta();
    }
  });

  // Lazy-каталог активной локали догрузился, а store-locale при этом НЕ менялся (его
  // выставили ВПЕРЁД каталога — `showLanding` ставит store синхронно + грузит каталог
  // fire-and-forget). Подписка выше молчит (store не изменился), хотя `t()` теперь отдаёт
  // верный язык. Перерисовываем активную ИГРОВУЮ фазу, иначе строки через `t()` остаются
  // на en-fallback рядом с уже-локализованными store-зависимыми (modeLabel — статичный
  // словарь) = СМЕШАННЫЕ языки до ручного переключения (owner-bug 2026-06-28, Архив).
  // rerenderActivePhaseForLocale сам исключает start/content и безопасен для транзиентных
  // фаз (рисуются из store) — тот же re-mount-приём, что boot (main.ts) и смена языка.
  // Системно (НЕ костыль под book): покрывает любой экран с `t()`+store-зависимыми строками
  // (Архив / лидерборд / лобби / профиль), мог бы мигать смешанными языками в том же окне.
  window.addEventListener(LOCALE_CATALOG_READY_EVENT, () => {
    rerenderActivePhaseForLocale();
  });

  // popstate (кнопки Назад/Вперёд браузера) — URL-МОДЕЛЬ B (ADR-0021 addendum-001):
  // читаем АКТУАЛЬНЫЙ URL и восстанавливаем поверхность, ТОТ ЖЕ порядок, что на boot
  // (реплей → 301-канон `/en/` → ГОЛЫЙ ЛЕНДИНГ → игровой → контент-статья). suppressUrlSync
  // на время применения → фаза-подписка не сделает повторный pushState. Деструктивно
  // для активной партии? Нет: Back из board ведёт на предыдущий URL, партия в кэше (T-B).
  window.addEventListener('popstate', () => {
    const p = window.location.pathname;
    suppressUrlSync = true;
    try {
      const replayId = parseReplayShortIdFromPath();
      const enCanon = canonicalEnRedirect(p);
      const landingLang = enCanon === null && replayId === null ? landingLangOfPath(p) : null;
      if (replayId) {
        useGameStore.getState().showReplayById(replayId);
      } else if (enCanon !== null) {
        // `/en/...` в истории → канонизируем на корень-вариант (replaceState). Голый
        // `/en/` → главная (start); `/en/<slug>/` → контент-статья.
        const land = landingLangOfPath(enCanon);
        if (land !== null) showLanding(land);
        else {
          const parsed = parseContentPath(enCanon);
          navigateContent(parsed.lang, parsed.slug, true);
        }
      } else if (landingLang !== null) {
        // ГОЛЫЙ ЛЕНДИНГ в истории (`/` или `/<lang>/`) — ДО игрового классификатора:
        // ЕДИНАЯ ГЛАВНАЯ (start-phase) на языке URL. Голый корень `/` (en) учитывает
        // §C.3: ru-браузер без персиста → главная на `/ru/`. showLanding/navigateLanding
        // сетят глобальный locale → стартовая + рейл следуют URL.
        if (isSiteRoot(p)) {
          const redirectTo = resolveRootRedirectSync();
          if (redirectTo) navigateLanding(redirectTo, true);
          else showLanding(landingLang);
        } else {
          showLanding(landingLang); // `/ru/` → ru-главная
        }
      } else {
        // LEGACY app-адрес в истории (`/gnomes`) → канонизируем на новый (`/companions`,
        // replaceState) ПЕРЕД классификацией, как на boot. Старый адрес из истории не
        // 404-ит (редкий кейс — запись могла остаться от до-переименования сессии).
        const legacy = legacyAppRouteRedirect(p);
        const gp = legacy ?? p;
        if (legacy !== null) window.history.replaceState({ game: legacy }, '', legacy);
        const game = classifyGamePath(gp);
        if (game) {
          applyGameRoute(game);
        } else if (isRetiredGamePath(p)) {
          // RETIRED игровой путь (`/workshop` снят, workshop-animals-removal.md O-3):
          // Back/Forward на него → graceful fallback на дефолт-поверхность (пикер) +
          // выправить адрес на `/play` (replaceState), НЕ контент-404 / белый экран.
          useGameStore.getState().showModeSelect();
          window.history.replaceState({ game: 'play' }, '', GAME_ROUTE_PATHS.play);
        } else {
          // Не лендинг (guard выше), не игра → контентная СТАТЬЯ (модель B). parse
          // всегда даёт {lang,slug} (slug≠'' — голый лендинг отсечён выше).
          const content = parseContentPath(p);
          useGameStore.getState().showContent(content.lang, content.slug);
        }
      }
      // SPA noindex-страховка на Back/Forward: URL уже актуален (popstate) →
      // привести meta-robots к нему (игровой путь → noindex, контент → снять).
      syncRobotsMeta(p);
    } finally {
      suppressUrlSync = false;
    }
  });

  mountPhase(useGameStore.getState().phase);
  syncActive();
  // Закрепить адрес стартовой фазы в истории (boot deep-link). replaceState (не новая
  // запись — это первый кадр) для игровых фаз; контент уже зафиксировал свой путь в
  // boot-роутинге (navigateContent/showContent). Этим первая запись истории несёт
  // корректный {game} state → первый Back ведёт куда надо.
  {
    const bootPath = pathForPhase(useGameStore.getState().phase);
    if (bootPath !== null && window.location.pathname.replace(/\/+$/, '') !== bootPath.replace(/\/+$/, '')) {
      window.history.replaceState({ game: bootPath }, '', bootPath);
    }
  }
  // SPA noindex-страховка на boot (deep-link сразу на игровой app-путь, напр.
  // `/leaderboard`): выставить meta-robots по итоговому пути ДО того, как
  // JS-краулер снимет снимок. Контент/лендинг — тег не ставится (индексируемы).
  syncRobotsMeta();
}

/**
 * Real nav-rail DESTINATIONS (single source). «Настройки» здесь НЕТ — это утилита
 * (шестерёнка) внизу рейла (§5.5, owner-decision 2026-06-04), заводится через
 * `utilities` в bootShell, чтобы не нарушать подобие «пункт рейла = страница».
 */
function realNavItems(): NavRailItem[] {
  const store = () => useGameStore.getState();
  return [
    // T-I18N-RAIL: КАЖДЫЙ пункт несёт КЛЮЧ каталога (labelKey), НЕ строку — рейл резолвит
    // t(labelKey) и перерисовывает подпись при смене языка (AC-1/AC-2). play/daily/
    // leaderboard REUSE существующих game.* ключей (НЕ дубль под rail).
    // play → resume the active unfinished run if one is cached (T-B / I-4 — straight into
    // the open partition), else the mode-selector lobby. `resumeOrLobby` is the single
    // source (shared by the boot path). Fire-and-forget (cache read async; store drives UI).
    { id: 'play', labelKey: 'game.play', icon: 'play', onSelect: () => void resumeOrLobby() },
    { id: 'daily', labelKey: 'game.daily', icon: 'calendar', onSelect: () => store().showDaily() },
    { id: 'leaderboard', labelKey: 'game.leaderboard', icon: 'trophy', onSelect: () => store().showLeaderboard() },
    // book — «Архив игр» (личный каталог записей партий, replay-collection.md / ADR-0023;
    // переименование owner 2026-06-24, было «Книга реплеев»). Подпись — каталог-ключ
    // `book.nav` (реактивна к языку, НЕ литерал). Иконка `archive` (папка + play-треугольник,
    // ICON_GAME_ARCHIVE — отлична от книжной иконки Help). Порядок: после leaderboard, перед gnomes.
    { id: 'book', labelKey: 'book.nav', icon: 'archive', onSelect: () => store().showBook() },
    // gnomes — активная мета v1 (meta-gnomes.md §5.1, ключ game.rail.gnomes): 7 гномов-добытчиков.
    { id: 'gnomes', labelKey: 'game.rail.gnomes', icon: 'gnomes', onSelect: () => store().showGnomes() },
    // workshop — пункт теперь DISABLED (серый, «скоро»), расположен ПОД gnomes
    // (fe-workshop-disabled.md AC-1/AC-4; workshop-animals-removal.md §2.1 O-1). Звери
    // удалены: экран снят. Переиспользует существующий disabled-примитив рейла
    // (`nav-item--disabled` + title=comingSoon) — БЕЗ onSelect, не кликабелен в экран.
    // Подпись — каталог-ключ `game.rail.workshop` (реактивна, НЕ литерал). Иконка
    // `workshop` (ICON_GEM) оставлена нейтральной заглушкой под будущие артефакты.
    { id: 'workshop', labelKey: 'game.rail.workshop', icon: 'workshop', disabled: true },
    { id: 'profile', labelKey: 'game.rail.profile', icon: 'user', onSelect: () => store().showProfile() },
    // support — «♥ Поддержать» (крипто-донат L0, ADR-0024 addendum). Подпись — каталог-
    // ключ `game.rail.support` (реактивна к языку, НЕ литерал). Иконка `heart`.
    { id: 'support', labelKey: 'game.rail.support', icon: 'heart', onSelect: () => store().showSupport() },
    // help (content) — вход в КОНТЕНТ-подстраницы (правила/гайд/стратегия/FAQ/об игре)
    // ВНУТРИ того же приложения, тот же рейл (ADR-0020 §«Сшить контент в приложение»;
    // owner-directive 2026-06-17: help = подробные описания, как у сапёра Help).
    // → дефолт-лендинг контента (navigateContent — единый ствол контент-роутинга +
    // pushState origin-relative). Не отдельный сайт: то же приложение, тот же origin.
    // help item → FIRST help article `how-to-play`; the second (TOC) rail walks the rest.
    // slug='' = `landing.md` = scroll-landing (the LOGO target) — the help entry must NOT
    // collide with the logo URL (owner-bug 2026-06-19: both logo and help opened /ru/).
    // Language = CURRENT global `locale` (empty '' fell back to DEFAULT_CONTENT_LANG='en').
    { id: 'content', labelKey: 'game.rail.help', icon: 'book', onSelect: () => navigateContent(store().locale, 'how-to-play') },
  ];
}

/**
 * Account-slot identity (account-profile-redesign.md §2): `null` для гостя (анонимный
 * mint без authMethod → пилюля «Войти»), иначе `{ playerId, displayName }` для chip.
 * Граница «гость» = `isAuthed() === false` (authMethod == null), как в §2 спеки.
 * displayName = ТОЛЬКО name-часть (ник или «Игрок»), БЕЗ `#`-тега (owner-directive
 * 2026-06-12: на рейле тег не показываем — он живёт в Профиле/лидерборде). Берём
 * `name` из общего shared-util `playerDisplayParts` (тот же источник, что лидерборд).
 */
function navRailAccountIdentity(): NavRailAccountIdentity | null {
  const cur = getCurrent();
  if (!cur || !isAuthed()) return null; // гость → пилюля «Войти».
  return {
    playerId: cur.playerId,
    displayName: playerDisplayParts(cur.displayName, cur.playerId).name,
  };
}

// Поверхность входа (auth-modal) + ОДНА общая logout-функция живут в `lib/auth-flow`
// (`openLoginFlow` / `logout`) — единый ствол на все вызовы (пилюля рейла / CTA Профиля
// / re-login). app.ts только монтирует их в рейл + reconcile slot через identity-events.

function mountDemoTeaser(host: HTMLElement): void {
  // Mock data — representative of what T-9 server will return for a TEASER
  // branch of `/r/<short_id>`. Includes ONLY public KPI; foundational
  // invariant Anti-shadow-practice (replays-ghosts.md §3A.3) forbids any
  // visual artefact of game (board / moves / final frame). Mock keeps it
  // schema-conformant.
  mountReplayTeaserScreen(
    host,
    {
      playerName: 'Игрок_42',
      scoreVerified: 14_200,
      rank: 3,
      movesCount: 47,
    },
    // onPlayDaily — owner-feedback 2026-05-26: CTA должен вести на главную
    // (она же daily-hub). Hard-navigate простейший unblock; T-9 заменит
    // на showDaily() store action когда teaser станет реальным /r/<id>.
    () => {
      window.location.assign('/');
    },
  );
}

function mountDemoLeaderboard(host: HTMLElement): void {
  mountLeaderboardScreen(host, leaderboardMockData());
}

/**
 * `?demo=replaysample` (help-screens-phone fix-cycle 5, owner playtest v6) — mounts the
 * REAL replay-screen with a VALID short sample replay so the «Помощи» illustration shows
 * the board WITH transport CONTROLS (the главная претензия of v6).
 *
 * The replay = `seed + moves[]` re-simulated by the CANON engine (replays-ghosts.md §2.1)
 * — the same source-of-truth as a real player's replay. We build the moves with
 * `genBotMoves` (canon bot), then truncate to a short prefix so the snapshot shows a
 * representative mid-game board (not empty start, not full end). The replay-screen
 * re-simulates from the seed and rewinds/steps — so the transport controls are live.
 */
function mountReplaySample(host: HTMLElement): void {
  // Stable demo seeds (not a daily / scored game — purely a representative replay).
  const SAMPLE_BOARD_SEED = 0x10b98;
  const SAMPLE_DECISION_SEED = 0x5a3c;
  const SAMPLE_PREFIX_MOVES = 6; // короткая партия — иллюстрация, не полный матч
  const { moves } = genBotMoves(SAMPLE_BOARD_SEED, SAMPLE_DECISION_SEED);
  mountReplayScreen(host, {
    source: {
      seed: SAMPLE_BOARD_SEED,
      moves: moves.slice(0, SAMPLE_PREFIX_MOVES),
      mode: 'predict',
    },
    // Harness page has nowhere to navigate; close (×) is a no-op for the screenshot.
    onExit: () => {},
  });
}

/**
 * `?demo=replaysave` (тикет B) — РЕАЛЬНЫЙ replay-screen с детерминированной сэмпл-партией
 * + (для `kind=foreign`) общий kit дискеты «Сохранить в Книгу» (lib/replay-save-button).
 * Зеркало `?demo=replaysample`, но с диск-проводкой + параметрами состояния. book-API
 * (POST /book/others/bookmark) мокается viewport-тестом; `&full=1` → 409 album_full →
 * овершлоу-попап. `&guest=1` → клик дискеты ведёт на логин (auth-modal).
 */
function mountDemoReplaySave(host: HTMLElement, params: URLSearchParams): void {
  const kind = params.get('kind') === 'own' ? 'own' : 'foreign';
  const saved = params.get('state') === 'saved';
  const guest = params.get('guest') === '1';
  // `&surface=teaser` (AC-B7) — монтировать ТИЗЕР чужой сегодняшней daily ВМЕСТО playable-
  // плеера (default = player). На тизере дискета закладывает чужую партию, хотя playable-
  // показ гейтится §3A до rollover — закладка ≠ ранний доступ (§6.4). `&full=1`/`&guest=1`
  // работают одинаково на обоих surface (овершлоу/логин решает kit по ответу мока).
  const surface = params.get('surface') === 'teaser' ? 'teaser' : 'player';

  // Кнопка плеера гейтится по `kind` (= server-truth isOwn в боевом remote-replay):
  //   · kind=own     → кнопка «Поделиться» (ownShare; share-глиф, copyShareLink /r/<id>);
  //   · kind=foreign → дискета «Сохранить» (foreignSave; чужой реплей, тикет B).
  // `&full=1` (5/5) монтаж не меняет — овершлоу решает СЕРВЕР (мок 409 на POST). `&guest=1`
  // на чужом → тап дискеты даёт попап-промпт «Войдите…» (НЕ редирект на логин, AC-3 гость).
  const ownShare: OwnShareOptions | undefined =
    kind === 'own' ? { shortId: 'demoown1' } : undefined;
  const foreignSave: ForeignSaveOptions | undefined =
    kind === 'foreign'
      ? {
          shortId: 'demoforeign1',
          savedInitially: saved,
          isGuest: guest,
          onRequestLogin: () => openLoginFlow(),
        }
      : undefined;

  if (surface === 'teaser') {
    // Тизер — text-only surface БЕЗ доски (anti-shadow §3A.3): мок-данные представительны
    // (счёт/ранг/ходы/отсчёт), как реальная TEASER-ветка /api/replay. Тот же общий kit
    // дискеты монтируется в шапку тизера для kind=foreign (на own — слот пуст).
    mountReplayTeaserScreen(
      host,
      {
        playerName: 'Игрок_42',
        scoreVerified: 14_200,
        rank: 3,
        movesCount: 47,
        countdownToRolloverSeconds: 5_400,
      },
      () => {},
      foreignSave,
    );
    return;
  }

  // Детерминированная сэмпл-партия (как ?demo=replaysample) — реальный seed+moves через
  // канон-движок (тот же источник, что боевой плеер); короткий префикс = представительная доска.
  const SAMPLE_BOARD_SEED = 0x10b98;
  const SAMPLE_DECISION_SEED = 0x5a3c;
  const SAMPLE_PREFIX_MOVES = 6;
  const { moves } = genBotMoves(SAMPLE_BOARD_SEED, SAMPLE_DECISION_SEED);

  mountReplayScreen(host, {
    source: {
      seed: SAMPLE_BOARD_SEED,
      moves: moves.slice(0, SAMPLE_PREFIX_MOVES),
      mode: 'predict',
    },
    onExit: () => {},
    ...(ownShare ? { ownShare } : {}),
    ...(foreignSave ? { foreignSave } : {}),
  });
}

/**
 * Demo/test-only: перехватить `/book/*` и отдавать ДЕТЕРМИНИРОВАННЫЕ данные Книги для
 * `?demo=book`. Зеркало того, как viewport-тесты `?demo=daily` мокают /daily/seed —
 * но здесь на стороне app, т.к. narrow-landscape тест мокает только /auth/anonymous.
 * Все НЕ-`/book` запросы (включая /auth/anonymous) делегируются исходному fetch (его
 * перехватывает Playwright route теста). Идемпотентно (ставим один раз за загрузку).
 * Удаляется вместе с прочими `?demo=*` ветками перед продом.
 *
 * Параметры — единый контракт демо-Книги (используют ОБА viewport-теста: старый
 * book.viewport.test через `&slice/&guest/&row`, новый narrow-landscape через `&long`):
 *   long   — ≥20 строк «Своих» (bounded inner-scroll переполняется, AC-2a-6);
 *   slice  — 'empty' → пустые срезы (empty-state, AC-A9); иначе 'filled';
 *   guest  — true → 401 на все /book (CTA логина, AC-A12);
 *   orphan — true → «Чужие» содержат одну осиротевшую строку (available=false, AC-A8).
 */
let bookDemoFetchInstalled = false;
function installBookDemoFetch(opts: {
  long: boolean;
  slice: 'empty' | 'filled';
  guest: boolean;
  orphan: boolean;
  guestCapped: boolean;
}): void {
  if (bookDemoFetchInstalled || typeof window === 'undefined') return;
  bookDemoFetchInstalled = true;
  const originalFetch = window.fetch.bind(window);
  const { long, slice, guest, orphan, guestCapped } = opts;

  // Детерминированный набор строк (метаданные — БЕЗ moves[]/seed, как ADR-0023).
  const ownCount = slice === 'empty' ? 0 : long ? 24 : 5;
  const makeRow = (i: number, album: 'own' | 'others'): Record<string, unknown> => ({
    short_id: `${album}demo${i}`,
    score_verified: 1000 - i * 17,
    moves_count: 40 + i,
    finished_at: `2026-06-${String((i % 27) + 1).padStart(2, '0')}T12:00:00.000Z`,
    mode: 'predict',
    is_daily: false,
    tab: 'predict',
    ...(album === 'others'
      ? { saved_at: `2026-06-${String((i % 27) + 1).padStart(2, '0')}T13:00:00.000Z`, available: true }
      : {}),
  });
  const ownRows = Array.from({ length: ownCount }, (_, i) => makeRow(i, 'own'));
  const othersRows: Record<string, unknown>[] =
    slice === 'empty'
      ? []
      : orphan
        ? [
            {
              short_id: 'orphandemo',
              score_verified: 0,
              moves_count: 0,
              finished_at: '2026-06-01T12:00:00.000Z',
              mode: 'predict',
              is_daily: false,
              tab: 'predict',
              saved_at: '2026-06-01T13:00:00.000Z',
              available: false,
            },
          ]
        : Array.from({ length: 3 }, (_, i) => makeRow(i, 'others'));
  const othersUsed = othersRows.length;

  const json = (body: unknown): Response =>
    new Response(JSON.stringify(body), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  const unauthorized = (): Response =>
    new Response(JSON.stringify({ ok: false, code: 'unauthorized' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const path = (() => {
      try {
        return new URL(url, window.location.origin).pathname;
      } catch {
        return url;
      }
    })();
    if (path.includes('/book/')) {
      if (guest) return Promise.resolve(unauthorized());
      if (path.includes('/book/capacity')) {
        return Promise.resolve(
          json({ own: { count: ownCount }, others: { used: othersUsed, capacity: 5 } }),
        );
      }
      if (path.includes('/book/own')) {
        // guest_capped — server-truth ТОЛЬКО «Свои» (G-2): демо-флаг `&guestcap=1`
        // рисует upsell-объяснение под списком (frontend-test-creator/owner проверка).
        // is_guest — server-truth «не зарегистрирован» (демо: гость при &guestcap=1 или &guest=1).
        return Promise.resolve(
          json({ items: ownRows, next_cursor: null, guest_capped: guestCapped, is_guest: guestCapped || guest }),
        );
      }
      if (path.includes('/book/others')) {
        return Promise.resolve(json({ items: othersRows, next_cursor: null }));
      }
    }
    // Всё остальное (incl. /auth/anonymous) — исходному fetch (Playwright route теста).
    return originalFetch(input as RequestInfo, init);
  };
}

/**
 * Mock leaderboard data — representative shape per core-daily-board.md §6.1 / §6.2.
 * Used by `?demo=leaderboard` (viewport tests) AND the in-shell `leaderboard` phase,
 * until T-8 wires the real `/daily/leaderboard` endpoint.
 */
function leaderboardMockData(): LeaderboardData {
  return {
    // Дата доски форматируется через ЕДИНЫЙ локализуемый formatDateLabel (Intl,
    // getLocale()) от ISO-даты — НЕ хардкод-RU «26 мая» (тот леакал кириллицу в
    // заголовок «Bestenliste · 26 мая» на всех языках). Demo-route грузит каталог +
    // сетит locale ДО монтажа → getLocale() уже = язык страницы здесь.
    dateLabel: formatDateLabel('2026-05-26'),
    initialTab: 'today',
    rowsToday: [
      // playerName = ник (полный размер); playerTag = `#`-суффикс (мельче+тихим) —
      // owner-decision 2026-06-09 «Vol #2c7e»: тег читается как подчинённый тег.
      // level = уровень-бейдж у КАЖДОГО игрока (owner-decision 2026-06-11) — варьируем,
      // чтобы демо/viewport показывали бейдж и у чужих строк (не только своей).
      { rank: 1, playerName: 'Игрок', playerTag: '7f0a', level: 27, scoreVerified: 21_400, movesCount: 38, isViewer: false },
      { rank: 2, playerName: 'Vol',   playerTag: '2c7e', level: 19, scoreVerified: 18_900, movesCount: 45, isViewer: false },
      // isViewer=true + shortId on own row → playable mode (3-я строка — Андрей,
      // owner-feedback 2026-05-26: реальный никнейм + «Вы»-badge, БЕЗ тега у себя).
      { rank: 3, playerName: 'Андрей', playerTag: 'a1b2', level: 12, scoreVerified: 14_200, movesCount: 52, isViewer: true, shortId: 'own123' },
      { rank: 4, playerName: 'Макс',  playerTag: 'f3ae', level: 8, scoreVerified: 11_800, movesCount: 41, isViewer: false },
      { rank: 5, playerName: 'Игрок', playerTag: '99c1', level: 5, scoreVerified:  9_750, movesCount: 39, isViewer: false },
      { rank: 6, playerName: 'Светлана', playerTag: '55d4', level: 3, scoreVerified:  8_400, movesCount: 47, isViewer: false },
      { rank: 7, playerName: 'Игрок', playerTag: '03ff', level: 1, scoreVerified:  6_900, movesCount: 36, isViewer: false },
    ],
    rowsYesterday: [
      { rank: 1, playerName: 'Игрок', playerTag: '77ab', level: 33, scoreVerified: 28_900, movesCount: 41, isViewer: false, shortId: 'abc123' },
      { rank: 2, playerName: 'Кристина', playerTag: '12cd', level: 21, scoreVerified: 22_100, movesCount: 44, isViewer: false, shortId: 'def456' },
      { rank: 3, playerName: 'Игрок', playerTag: '88ef', level: 14, scoreVerified: 19_400, movesCount: 50, isViewer: false, shortId: 'ghi789' },
      { rank: 4, playerName: 'Андрей', playerTag: 'a1b2', level: 12, scoreVerified: 15_600, movesCount: 38, isViewer: true,  shortId: 'jkl012' },
    ],
  };
}

/**
 * `?demo=rail` — ВИЗУАЛЬНОЕ демо глобального nav-rail (owner просил «посмотреть
 * сначала» перед проводкой в навигацию). Монтирует app-shell рейл + реальную
 * (casual) доску в content-slot, чтобы owner увидел: рейл inline слева, доска
 * по центру и НЕ ужата, выезд ☰ на узком. Пункты пока шлют toast (проводка в
 * реальные экраны — этап интеграции, не этого look-демо). Вход — реальная auth-modal.
 */
function mountDemoRail(host: HTMLElement): void {
  // Демо-toast подпись резолвится из каталога t(labelKey) — НЕ RU-литерал (T-I18N-RAIL
  // AC-1: рейл-секции app.ts без хардкод-подписей). Это look-демо: реальная проводка в
  // экраны — этап интеграции, не демо.
  const navAction = (labelKey: string) => () => showToast(t(labelKey));
  const rail = mountNavRail(host, {
    activeId: 'play',
    // T-I18N-RAIL: demo-рейл несёт КЛЮЧИ каталога (labelKey) — тот же механизм, что
    // realNavItems (НЕ отдельный хардкод-путь). reuse game.* + game.rail.profile.
    items: [
      { id: 'play', labelKey: 'game.play', icon: 'play', onSelect: navAction('game.play') },
      { id: 'daily', labelKey: 'game.daily', icon: 'calendar', onSelect: navAction('game.daily') },
      { id: 'leaderboard', labelKey: 'game.leaderboard', icon: 'trophy', onSelect: navAction('game.leaderboard') },
      { id: 'profile', labelKey: 'game.rail.profile', icon: 'user', disabled: true },
      { id: 'settings', labelKey: 'game.settings', icon: 'settings', disabled: true },
    ],
    // Account-slot: гость → login-пилюля (открывает auth-modal, единый auth-flow).
    // Reconcile slot — через подписку на identity-events ниже.
    account: navRailAccountIdentity(),
    accountCallbacks: {
      onLogin: () => openLoginFlow(),
      onLogout: () => authLogout(),
    },
  });
  onIdentityChanged(() => rail.setAccountState(navRailAccountIdentity()));

  // Реальная доска в content-slot (casual, без сети) — доска центрируется сама,
  // рейл оверлеем слева её не трогает (board-as-etalon). ФИКСИРОВАННЫЙ seed (?seed=
  // override иначе) → детерминированная раскладка для visual-baseline'ов рейла
  // (i18n-rail-switcher.viewport): без фикс-seed случайные шары давали diff между
  // прогонами и ломали screenshot-сравнение (board занимает кадр демо).
  const demoSeed = parseSeedFromQuery() ?? 0x1ace98;
  useGameStore.getState().startNewGame(demoSeed);
  mountBoardScreen(rail.contentSlot);
}

function mountPhase(phase: Phase, force = false): void {
  if (!runtime) return;
  // force=true: перемонтировать ДАЖЕ ту же фазу. Нужно для контента: переход между
  // разделами остаётся в фазе 'content' (phase не меняется), но contentTarget другой —
  // без force ранний выход не давал обновить текст (owner-playtest 2026-06-17).
  if (!force && runtime.current?.phase === phase) return;

  // Unmount previous.
  if (runtime.current) {
    runtime.current.handle.unmount();
    runtime.current = null;
  }

  // Mount new.
  switch (phase) {
    case 'start':
      // Стартовая страница (минимальный лендинг — owner-directive 2026-06-17): тонкая
      // ветка (логотип-герой + 3 карточки casual + SEO-хук). NO board/engine (минимализм).
      runtime.current = { handle: mountStartScreen(runtime.host), phase };
      return;
    case 'daily':
      runtime.current = { handle: mountDailyBoardScreen(runtime.host), phase };
      return;
    case 'modeSelect':
      // Mode selector (extra-modes §5.1) — a light branch screen; NO board on the
      // scene (C1). Card-click → POST /game/start + startAllTimeGame → board phase.
      runtime.current = { handle: mountModeSelectScreen(runtime.host), phase };
      return;
    case 'board':
      runtime.current = { handle: mountBoardScreen(runtime.host), phase };
      return;
    case 'result':
      runtime.current = { handle: mountResultScreen(runtime.host), phase };
      return;
    case 'replay': {
      const shortId = useGameStore.getState().replayShortId;
      // Remote replay (deep-link / in-shell leaderboard nav) → fetch + branch by
      // server `kind`. Own last-game replay (`replayShortId === null`) → mount the
      // local player directly (no fetch).
      runtime.current = {
        handle: shortId
          ? mountRemoteReplay(runtime.host, shortId)
          : mountReplayScreen(runtime.host),
        phase,
      };
      return;
    }
    case 'leaderboard': {
      // In-shell leaderboard — REAL data. Daily (default) OR all-time per mode
      // (extra-modes §5.4) when `leaderboardAllTimeMode` is set (from
      // `showLeaderboard(mode)`). Mock stays only in `?demo=leaderboard`.
      const allTimeMode = useGameStore.getState().leaderboardAllTimeMode ?? undefined;
      runtime.current = {
        handle: mountLeaderboardScreen(runtime.host, undefined, allTimeMode),
        phase,
      };
      return;
    }
    case 'settings':
      // In-shell settings page (brief-39 §C) — game-agnostic surface; back via the
      // screen's «Назад» / the nav-rail.
      runtime.current = { handle: mountSettingsScreen(runtime.host), phase };
      return;
    // case 'workshop' УДАЛЁН вместе с «животными» (workshop-animals-removal.md): фазы
    // 'workshop' больше нет (store Phase), экран зверей снят, пункт рейла disabled.
    case 'gnomes': {
      // In-shell gnomes plaque (плашка «Гномы» — meta-surface, GN-1) — game-agnostic
      // surface; 7 gnome cards (placeholder until GN-4 server block) + balances from
      // GET /meta/state. Back via the nav-rail.
      // companion-focus AC-1: deep-link фокус на конкретном помощнике — читаем store-payload
      // (mirror leaderboardAllTimeMode) и передаём в экран; экран потребит one-shot. Без
      // прямого History/URL — фокус-цель приходит ТОЛЬКО через store.
      const focusColor = useGameStore.getState().gnomesFocusColor ?? undefined;
      runtime.current = { handle: mountGnomesScreen(runtime.host, { focusColor }), phase };
      return;
    }
    case 'profile':
      // In-shell profile hub (хаб-Профиль — meta-surface, Срез 4b) — game-agnostic
      // showcase; reads GET /meta/state (+ progression) server-truth. Back via nav-rail.
      runtime.current = { handle: mountProfileScreen(runtime.host), phase };
      return;
    case 'book': {
      // In-shell «Книга реплеев» (личный каталог реплеев — replay-collection.md /
      // ADR-0023) — account-bound surface; reads GET /book/* server-truth, открывает
      // партию общим replay-flow (gate §3A). Back via nav-rail. Deep-link / demo может
      // задать стартовый альбом через `?album=own|others` (как `?demo=book&album=`).
      const albumParam = new URLSearchParams(window.location.search).get('album');
      const initialAlbum = albumParam === 'others' ? 'others' : albumParam === 'own' ? 'own' : undefined;
      runtime.current = {
        handle: mountBookScreen(runtime.host, initialAlbum ? { initialAlbum } : {}),
        phase,
      };
      return;
    }
    case 'support':
      // In-shell «Поддержать» (крипто-донат L0, ADR-0024 addendum) — game-agnostic
      // meta-surface: 3 крипто-карты приёма (USDT/Ethereum/Bitcoin) из lib/donate-targets.
      // Donation gives nothing in-game (no pay-to-win) — server never learns of it.
      runtime.current = { handle: mountSupportScreen(runtime.host), phase };
      return;
    case 'content':
      // In-shell content page (лендинг/правила/гайд/стратегия/FAQ/about) — тот же
      // контент, что краулер видит в статике (ADR-0020 §«Сшить контент в приложение»),
      // тот же рейл/оболочка, один origin. Цель — store `contentTarget` (lang+slug).
      runtime.current = { handle: mountContentScreen(runtime.host), phase };
      return;
  }
}

/**
 * Ре-монтировать активную ВНУТРИ-ОБОЛОЧЕЧНУЮ ИГРОВУЮ фазу по ГОТОВНОСТИ каталога
 * локали на boot (owner-bug 2026-06-24, root-cause «b»: F5 / жёсткий рефреш на `/book`).
 *
 * ЗАЧЕМ: на cold-boot фаза монтируется СИНХРОННО (`mountApp`) ДО готовности lazy-каталога
 * (`bootInitialLocale` — fire-and-forget в main.ts) → первый кадр игрового экрана на
 * СЫРЫХ ключах `t()` (каталог ещё не загружен). Store-подписка ре-монтирует фазу на смену
 * `locale`, НО когда резолв-язык СОВПАДАЕТ со store-дефолтом (`en`-юзер / без явного
 * выбора), `applyLocale('en')` → `setLocale('en')` НЕ меняет значение → подписка молчит →
 * экран остаётся на сырых ключах навсегда. Поэтому main.ts зовёт это ПО РЕЗОЛВУ
 * `bootInitialLocale` (каталог гарантированно загружен) — принудительный re-mount активной
 * игровой фазы рисует лейблы/список заново на верном языке. Идемпотентно (если фаза уже
 * перемонтировалась подпиской на смену значения — повтор лишь перерисует те же строки).
 *
 * start/content ИСКЛЮЧЕНЫ: start-копия лежит в бандле (синхронно верна, см. `showLanding`),
 * content управляет своим re-mount через `contentChanged`/собственный поток. Транзиентные
 * фазы (result/replay/board) тоже покрыты — у них подписи через `t()`, на boot-deep-link
 * это безопасный no-op (они рисуются из store, как при штатном re-mount).
 */
export function rerenderActivePhaseForLocale(): void {
  if (!runtime?.current) return;
  const phase = runtime.current.phase;
  if (phase === 'start' || phase === 'content') return;
  mountPhase(phase, /* force */ true);
}

